import {
  collection, addDoc, query, where, orderBy, limit,
  getDocs, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTION = 'activityLogs';

export const LOG_TYPES = {
  USER_APPROVED: 'user_approved',
  USER_REJECTED: 'user_rejected',
  USER_REMOVED: 'user_removed',
  USER_PROMOTED: 'user_promoted',
  EVENT_CREATED: 'event_created',
  EVENT_DELETED: 'event_deleted',
  REGISTRATION_CREATED: 'registration_created',
  REGISTRATION_CANCELLED: 'registration_cancelled',
};

export const LOG_LABELS = {
  user_approved: 'Volunteer approved',
  user_rejected: 'Volunteer rejected',
  user_removed: 'Member removed',
  user_promoted: 'Promoted to co-admin',
  event_created: 'Event created',
  event_deleted: 'Event deleted',
  registration_created: 'Signed up for event',
  registration_cancelled: 'Cancelled registration',
};

/**
 * Write an activity log entry.
 * @param {object} params
 * @param {string} params.organizationId
 * @param {string} [params.organizationName]
 * @param {string} params.type - one of LOG_TYPES
 * @param {string} params.actorId - uid of the user who performed the action
 * @param {string} params.actorName
 * @param {string} [params.targetId] - uid or event id of the subject
 * @param {string} [params.targetName]
 * @param {object} [params.metadata]
 */
export const logActivity = async ({
  organizationId, organizationName = null, type, actorId, actorName,
  targetId = null, targetName = null, metadata = {},
}) => {
  try {
    await addDoc(collection(db, COLLECTION), {
      organizationId,
      organizationName,
      type,
      actorId,
      actorName,
      targetId,
      targetName,
      metadata,
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    // Non-fatal — never let logging break core flows
    console.warn('Failed to write activity log:', err.message);
  }
};

/**
 * Fetch activity logs for a single organization (org-admin view).
 */
export const getActivityLogs = async (organizationId, days = 30) => {
  const since = Timestamp.fromDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));
  const q = query(
    collection(db, COLLECTION),
    where('organizationId', '==', organizationId),
    where('timestamp', '>=', since),
    orderBy('timestamp', 'desc'),
    limit(200),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Fetch activity logs across all organizations (platform-admin view).
 * Optionally filter by a specific org.
 */
export const getAllActivityLogs = async (days = 30, filterOrgId = null) => {
  const since = Timestamp.fromDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));
  const constraints = [
    where('timestamp', '>=', since),
    orderBy('timestamp', 'desc'),
    limit(500),
  ];
  if (filterOrgId) {
    constraints.unshift(where('organizationId', '==', filterOrgId));
  }
  const q = query(collection(db, COLLECTION), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};
